import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { listLeadersAdmin, reorderLeaders, deleteLeader } from "@/lib/api/leaders.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/leaders/")({
  loader: () => listLeadersAdmin(),
  component: AdminLeadersList,
});

type Row = Awaited<ReturnType<typeof listLeadersAdmin>>[number];

function AdminLeadersList() {
  const initial = Route.useLoaderData();
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function persistOrder(next: Row[]) {
    setBusy(true);
    try {
      await reorderLeaders({ data: { ids: next.map((r) => r.id) } });
      setRows(next);
      toast.success("Order updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: Row) {
    try {
      await deleteLeader({ data: { id: row.id } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Leader deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    { accessorKey: "designation", header: "Designation" },
    { accessorKey: "slug", header: "Slug", meta: { filterVariant: "text" } },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", groupable: true },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/leaders/$id", params: { id: row.original.id } }}
          onDelete={() => handleDelete(row.original)}
          deleteLabel={row.original.name}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Leaders"
        action={
          <Button asChild>
            <Link to="/admin/leaders/new">
              <Plus className="mr-2 h-4 w-4" /> New leader
            </Link>
          </Button>
        }
      />
      <DataTable
        tableId="leaders"
        columns={columns}
        data={rows}
        enableRowReorder
        onReorder={persistOrder}
        reorderDisabled={busy}
      />
    </AdminPageShell>
  );
}
